/**
 * The time-of-day plan: how many rounds the day has room for, from the slot times and the last
 * whistle. An estimate for the header, never a promise: rounds that finish early pull everything
 * forward, a pause pushes it back.
 *
 * With rounds started by an admin (decision 41) a round also waits for the courts to be ready
 * after its changeover. Once two rounds have started, that wait, as measured, is added to every
 * slot still to come; rounds faster than the game and the changeover never make a slot shorter
 * than those two. "N more rounds fit", the wall's "round 6 of about 16" and the pace line on the
 * Rounds tab ("about 9 games each") all take their count from dayPlan below, so they count the
 * day the same way.
 */

import type { TournamentState } from "./types";

export interface DayPlan {
  roundsSoFar: number;
  /** Rounds still to start after the current one. */
  roundsLeft: number;
  /** Rounds so far plus rounds left. */
  totalExpected: number;
  /** When the next round is expected to start. */
  nextRoundStartsAt: number | null;
  /** When the last round of the day is expected to start, or null when none is left. */
  lastRoundStartsAt: number | null;
}

/**
 * How long a round has really been taking: the lower median of the gaps between the last five
 * starts, or null before there are two. A median, so one lunch does not read as slow rounds.
 * With rounds started by an admin (decision 41) the gap includes the wait for the courts to be
 * ready, which is what the day really runs at.
 */
export function actualSlotMs(state: TournamentState, last = 4): number | null {
  const starts = state.rounds.slice(-(last + 1)).map((r) => r.startedAt);
  if (starts.length < 2) return null;
  const gaps = starts.slice(1).map((at, i) => at - starts[i]!).sort((x, y) => x - y);
  return gaps[Math.floor((gaps.length - 1) / 2)]!;
}

/**
 * With rounds started by an admin: how much longer than the game and the changeover a round has
 * been taking, the wait for the courts and the Start. 0 with rounds starting by themselves, or
 * before two rounds have started.
 */
export function startWaitMs(state: TournamentState): number {
  if (state.config.autoAdvance) return 0;
  const actual = actualSlotMs(state);
  return actual === null ? 0 : Math.max(0, actual - (state.config.gameMs + state.config.changeoverMs));
}

/** `waitMs`: the wait added to each slot; the state's own unless given (what other slot times buy keeps today's wait). */
export function dayPlan(state: TournamentState, now: number, waitMs = startWaitMs(state)): DayPlan {
  const { gameMs, changeoverMs, lastWhistleAt } = state.config;
  const slot = gameMs + changeoverMs + waitMs;
  const roundsSoFar = state.rounds.length;
  const paused = state.pausedAll && state.clock.pausedAt !== null ? now - state.clock.pausedAt : 0;
  let nextStart: number;
  switch (state.clock.phase) {
    case "playing":
      nextStart = (state.clock.phaseEndsAt ?? now) + paused + changeoverMs + waitMs;
      break;
    case "changeover":
      nextStart = (state.clock.phaseEndsAt ?? now) + paused + waitMs;
      break;
    default:
      nextStart = now;
  }
  if (lastWhistleAt === null) {
    return { roundsSoFar, roundsLeft: 0, totalExpected: roundsSoFar, nextRoundStartsAt: nextStart, lastRoundStartsAt: null };
  }
  let roundsLeft = 0;
  let last: number | null = null;
  for (let start = nextStart; start + gameMs <= lastWhistleAt; start += slot) {
    roundsLeft += 1;
    last = start;
  }
  return {
    roundsSoFar,
    roundsLeft,
    totalExpected: roundsSoFar + roundsLeft,
    nextRoundStartsAt: roundsLeft ? nextStart : null,
    lastRoundStartsAt: last,
  };
}
